import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fmtMoney } from '../utils/currency';

const Receipt = ({ order, onClose, onPrint }) => {
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    axios.get('/api/config').then((res) => setBusiness(res.data?.business)).catch(() => {});
  }, []);

  if (!order) return null;

  const handlePrint = () => {
    window.print();
    if (onPrint) onPrint();
  };

  const businessName = business?.name || 'Chenab System';
  const contactLine = [business?.address, business?.phone].filter(Boolean).join(' · ');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 print:bg-white print:inset-auto print:relative">
      <div className="bg-white p-8 rounded-lg shadow-card-hover max-w-sm w-full font-mono text-sm print:shadow-none print:rounded-none">
        <div className="text-center mb-6">
          <div className="w-10 h-10 mx-auto bg-gradient-to-br from-primary-500 to-primary-700 text-white rounded-lg flex items-center justify-center font-bold mb-2">
            {businessName.charAt(0).toUpperCase()}
          </div>
          <h2 className="text-lg font-bold tracking-wide uppercase">{businessName}</h2>
          {contactLine && <p className="text-xs text-neutral-500 mt-1">{contactLine}</p>}
        </div>

        <div className="border-t border-b border-dashed border-neutral-300 py-3 mb-4">
          <div className="flex justify-between mb-2">
            <span className="font-semibold">Order #:</span>
            <span>{order.orderNumber}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="font-semibold">Date:</span>
            <span>{new Date(order.createdAt).toLocaleString()}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="font-semibold">Type:</span>
            <span className="capitalize">{order.orderType}</span>
          </div>
          {order.tableNumber && (
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Table:</span>
              <span>{order.tableNumber}</span>
            </div>
          )}
          {order.customer?.name && (
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Customer:</span>
              <span>{order.customer.name}</span>
            </div>
          )}
        </div>

        <div className="mb-4 space-y-1.5">
          {order.items.map((item, index) => {
            const addOnsSum = (item.selectedAddOns || []).reduce((s, a) => s + (Number(a.price) || 0), 0);
            const lineTotal = (item.price + addOnsSum) * item.quantity;
            return (
              <div key={index} className="py-1">
                <div className="flex justify-between">
                  <div className="flex-1 min-w-0">
                    <p>{item.quantity}× {item.menuItem?.name || 'Item'}</p>
                  </div>
                  <span className="ml-2 tabular-nums">{fmtMoney(lineTotal, true)}</span>
                </div>
                {item.spiceLevel && (
                  <p className="text-[11px] text-neutral-600 ml-3">
                    · {item.spiceLevel.replace('_', ' ')}
                  </p>
                )}
                {(item.selectedAddOns || []).map((a, ai) => (
                  <div key={ai} className="flex justify-between text-[11px] text-neutral-600 ml-3">
                    <span>+ {a.name}</span>
                    <span className="tabular-nums">{fmtMoney(Number(a.price) || 0, true)} ea</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="border-t border-dashed border-neutral-300 space-y-1.5 pt-3 mb-3">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span className="tabular-nums">{fmtMoney(order.totalAmount, true)}</span>
          </div>
          {order.tipAmount > 0 && (
            <div className="flex justify-between">
              <span>Tip</span>
              <span className="tabular-nums">{fmtMoney(order.tipAmount, true)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-base border-t border-dashed border-neutral-400 pt-2 mt-2">
            <span>TOTAL</span>
            <span className="tabular-nums">{fmtMoney(order.totalAmount + (order.tipAmount || 0), true)}</span>
          </div>
        </div>

        <div className="border-t border-dashed border-neutral-300 pt-3 mb-4 space-y-1">
          <div className="flex justify-between">
            <span>Payment</span>
            <span className="capitalize">{order.paymentMethod?.replace('_', ' ')}</span>
          </div>
          {order.paymentMethod === 'cash' && order.amountReceived && (
            <>
              <div className="flex justify-between">
                <span>Cash received</span>
                <span className="tabular-nums">{fmtMoney(order.amountReceived, true)}</span>
              </div>
              <div className="flex justify-between">
                <span>Change</span>
                <span className="tabular-nums">{fmtMoney(order.changeGiven || 0, true)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between font-bold text-success-600">
            <span>STATUS</span>
            <span>PAID ✓</span>
          </div>
        </div>

        <div className="text-center text-xs text-neutral-500 mb-4 border-t border-dashed border-neutral-300 pt-3">
          {order.orderType === 'dine-in' && <p>Thank you for dining with us!</p>}
          {order.orderType === 'takeaway' && <p>Thank you for your order — enjoy!</p>}
          {order.orderType === 'delivery' && <p>Thank you — your order is on its way!</p>}
          {order.orderType === 'drive-thru' && <p>Thank you — drive safe!</p>}
          <p>Please come again</p>
        </div>

        <div className="flex gap-4 print:hidden">
          <button
            onClick={handlePrint}
            className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
          >
            Print Receipt
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-700 py-2 rounded hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Receipt;
